import { useState, useMemo } from 'react';
import { isEqual } from 'lodash';

/* eslint-disable no-shadow */
type Selectable = string | { id: string | number };

const useSelectionsArray = <T extends Selectable>(items: T[], defaultSelected: T[] = [], singleMode = false) => {
  const [selected, setSelected] = useState<T[]>(defaultSelected);

  const { selectedSet, isSelected, select, unSelect, toggle } = (() => {
    const selectedSet = [...selected];
    const isSelected = (item: T) => {
      return selectedSet.some((o) => isEqual(o, item));
    };

    const select = (item: T) => {
      return setSelected([...selectedSet, item]);
    };

    const unSelect = (item: T) => {
      const newSelectedSet = selectedSet.filter((o) => {
        return !isEqual(o, item);
      });

      return setSelected(newSelectedSet);
    };

    const toggle = (item: T) => {
      if (isSelected(item)) {
        unSelect(item);
      } else {
        if (singleMode) {
          selectedSet.splice(0, selectedSet.length);
          setSelected(selectedSet);
        }
        select(item);
      }
    };

    return { selectedSet, isSelected, select, unSelect, toggle };
  })();

  const {
    selectAll,
    unSelectAll,
    noneSelected,
    allSelected,
    partiallySelected,
    toggleAll,
  } = useMemo(() => {
    const selectAll = () => {
      if (singleMode) {
        throw new Error('Forbid to use selectAll method in single mode');
      }

      items.forEach((item) => {
        if (typeof item === 'string' && !selectedSet.includes(item)) {
          selectedSet.push(item);
        }

        if (typeof item !== 'string' && !selectedSet.find((selectedItem) => typeof selectedItem !== 'string' && selectedItem.id === item.id)) {
          selectedSet.push(item);
        }
      });
      setSelected(selectedSet);
    };

    const unSelectAll = () => {
      selectedSet.splice(0, selectedSet.length);
      setSelected(selectedSet);
    };

    const noneSelected = items.every((item) => {
      if (typeof item === 'string') {
        return !selectedSet.includes(item);
      }

      return !selectedSet.find((selectedItem) => typeof selectedItem !== 'string' && selectedItem.id === item.id);
    });

    const allSelected = items.every((item) => {
      if (typeof item === 'string') {
        return selectedSet.includes(item) && !noneSelected;
      }

      return selectedSet.some((selectedItem) => typeof selectedItem !== 'string' && selectedItem.id === item.id) && !noneSelected;
    });

    const partiallySelected = !noneSelected && !allSelected;

    const toggleAll = () => (allSelected ? unSelectAll() : selectAll());

    return { selectAll, unSelectAll, noneSelected, allSelected, partiallySelected, toggleAll };
  }, [items, selectedSet, singleMode]);

  return {
    selected,
    isSelected,
    select,
    unSelect,
    toggle,
    selectAll,
    unSelectAll,
    toggleAll,
    allSelected,
    noneSelected,
    partiallySelected,
    setSelected,
  };
};

export default useSelectionsArray;
